import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Building2, Briefcase, Phone, Send, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSound } from '../hooks/useSound';

const industries = [
  'Technology',
  'Manufacturing',
  'Retail',
  'Healthcare',
  'Financial Services',
  'Professional Services',
  'Real Estate',
  'Construction',
  'Education',
  'Other'
];

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

const STORAGE_KEY = 'consultation_form_data';
// Ensure URL is properly formatted with https://
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzMSeA-aFWcCX6YQJyTo58jUvVVi83GFHA5SLnTnUDR0unZwviAbkkAJ-pTLf3JmW-5LQ/exec';

export const ConsultationForm = () => {
  const navigate = useNavigate();
  const { playHover, playClick } = useSound();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    industry: '',
    phone: '',
    email: '',
    message: ''
  });

  // Load saved form data on mount
  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(parsed);
      } catch (e) {
        console.error('Failed to parse saved form data:', e);
      }
    }
  }, []);

  // Save form data when it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  const validateForm = () => {
    // Trim all form values
    const trimmedData = Object.entries(formData).reduce((acc, [key, value]) => ({
      ...acc,
      [key]: typeof value === 'string' ? value.trim() : value
    }), {} as typeof formData);

    setFormData(trimmedData);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[\d\s-()]+$/;

    if (!trimmedData.name) {
      throw new Error('Please enter your full name');
    }

    if (trimmedData.name.length < 2) {
      throw new Error('Please enter your full name');
    }

    if (!trimmedData.email) {
      throw new Error('Please enter your email address');
    }

    if (!emailRegex.test(trimmedData.email)) {
      throw new Error('Please enter a valid email address');
    }

    if (!trimmedData.phone) {
      throw new Error('Please enter your phone number');
    }

    if (!phoneRegex.test(trimmedData.phone)) {
      throw new Error('Please enter a valid phone number');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) return;

    setError(null);
    setSuccess(false);
    setLoading(true);
    playClick();

    try {
      validateForm();

      const payload = {
        ...formData,
        submittedAt: new Date().toISOString(),
        source: window.location.href,
        userAgent: navigator.userAgent,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      // Verify URL is valid before making the request
      const url = new URL(GOOGLE_SCRIPT_URL);
      
      const response = await fetch(url.toString(), {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      // Clear saved form data
      localStorage.removeItem(STORAGE_KEY);

      // Show success message briefly before redirecting
      setSuccess(true);
      
      // Short delay to show success message
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Navigate to thank you page with form data
      navigate('/thank-you', { state: { formData } });
    } catch (err) {
      if (err instanceof Error && err.message.includes('URL')) {
        setError('We have received your submission.');
      } else {
        setError(err instanceof Error ? err.message : 'We have received your submission.');
      }
      console.error('Form submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  return (
    <motion.form
      onSubmit={handleSubmit}
      className="space-y-6 p-8 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm"
    >
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-amber-400/10 border border-amber-400/20 rounded-lg text-amber-400 text-center"
        >
          {error}
        </motion.div>
      )}

      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-center"
        >
          Thank you! Redirecting you to the next step...
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium text-gray-300">
            Full Name
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400" />
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              maxLength={100}
              className="w-full pl-10 pr-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300"
              placeholder="John Doe"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="company" className="block text-sm font-medium text-gray-300">
            Company Name
          </label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400" />
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              maxLength={100}
              className="w-full pl-10 pr-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300"
              placeholder="Company Inc."
            />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="industry" className="block text-sm font-medium text-gray-300">
            Industry
          </label>
          <div className="relative">
            <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400" />
            <select
              id="industry"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              className="w-full pl-10 pr-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300 appearance-none"
            >
              <option value="" disabled>Select your industry</option>
              {industries.map(industry => (
                <option key={industry} value={industry} className="bg-background text-white">
                  {industry}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="phone" className="block text-sm font-medium text-gray-300">
            Phone Number
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400" />
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              maxLength={20}
              className="w-full pl-10 pr-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300"
              placeholder="+1 (555) 000-0000"
            />
          </div>
        </div>

        <div className="space-y-2 md:col-span-2">
          <label htmlFor="email" className="block text-sm font-medium text-gray-300">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400" />
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              maxLength={100}
              className="w-full pl-10 pr-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300"
              placeholder="john@company.com"
            />
          </div>
        </div>

        <div className="space-y-2 md:col-span-2">
          <label htmlFor="message" className="block text-sm font-medium text-gray-300">
            Additional Information (Optional)
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            maxLength={1000}
            className="w-full px-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300"
            placeholder="Tell us about your business needs..."
          />
        </div>
      </div>

      <motion.button
        type="submit"
        disabled={loading}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={springConfig}
        onMouseEnter={playHover}
        className="w-full px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold text-lg hover:shadow-lg hover:shadow-amber-400/20 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            Submit
          </>
        )}
      </motion.button>

      <div className="text-center text-gray-400 text-sm">
        Having trouble? Email us directly at{' '}
        <a 
          href="mailto:info@profitiq.ai" 
          className="text-amber-400 hover:text-amber-300 transition-colors duration-300"
        >
          info@profitiq.ai
        </a>
      </div>
    </motion.form>
  );
};